import { useMemoizedFn } from 'ahooks';
import { useSearchParams } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { useRef } from 'react';
import { reportTrack } from '@/app/components/shared/ClientInit/update-track';
import { toast } from '@/app/components/ui/use-toast';
import { isEnterpriseDistribution, SITE } from '@/app/constants';
import { useClaw, useCredits, useFetch, useSubscription, useTrials, useUserInfo } from '@/app/hooks';
import { useStore } from '@/app/providers/store';
import {
  createPendingRequestGate,
  getImpactImRef,
  getXhsClickId,
  reportEvent,
  sensorsSignUp,
  setRegisterPage,
} from '@/app/utils';
import { getDeviceId, processReferrer, trackMetaCompleteRegistration } from '@/app/utils/client';
import { useRouter } from '@/i18n/routing';
import { usersLogin, usersSendAuthCode, usersVerifyCaptchaGate } from '../apis';
import { LOGIN_ERROR_INTL_PREFIX, VALID_FROM_PAGES } from '../constants';
import type { LoginInfo } from '../types';
import { getCustomPlatformLoginErrorMessage } from '../utils';

interface UseVerifyCodeLoginProps {
  onSendVerifyCode: (success: boolean, errorMsg: string) => void;
  onSignInWithVerifyCode: (success: boolean, errorMsg: string) => void;
  onSetCodeError: (codeError: boolean) => void;
  onCustomPlatformReason?: (reason?: string) => boolean;
  skipPostLoginInitialization?: boolean;
}

export const useVerifyCodeLogin = ({
  onSendVerifyCode,
  onSignInWithVerifyCode,
  onSetCodeError,
  onCustomPlatformReason,
  skipPostLoginInitialization = false,
}: UseVerifyCodeLoginProps) => {
  const { updateUserInfo } = useStore((state) => ({
    updateUserInfo: state.updateUserInfo,
  }));
  const t = useTranslations();
  const router = useRouter();
  const searchParams = useSearchParams();
  const loginSendVerifyCodeInfo = useRef<LoginInfo>({ type: 'email', value: '' });
  const pendingSignInGateRef = useRef(createPendingRequestGate());
  // Guards the CAPTCHA flow against re-entry while the async captcha-gate +
  // retry chain is in flight, so repeated button clicks cannot spawn parallel
  // challenges or mix stale captcha_ids with changed inputs.
  const captchaInFlightRef = useRef(false);
  const { updateCreditsAsync } = useCredits();
  const { updateSubscriptionAsync } = useSubscription();
  const { updateTrialsAsync } = useTrials();
  const { fetchTrialExpirationAsync, fetchAvailableNexusAsync, fetchAgentInviteInfoAsync } = useUserInfo();
  const { getMyClawsAsync } = useClaw();

  const { loading: sending, run: sendVerifyCode } = useFetch(
    (loginInfo: LoginInfo, _?: () => void) => {
      loginSendVerifyCodeInfo.current = loginInfo;
      const channel = loginInfo.type === 'email' ? 'email' : 'sms';
      return usersSendAuthCode({
        body: {
          channel,
          receiver: loginInfo.value,
          purpose: 'login',
          locale: 'en',
          customer_code: loginInfo.customerCode,
        },
      });
    },
    {
      manual: true,
      onSuccess: ({ error }, [_, callback]) => {
        let errorMsg = '';
        if (error) {
          const customPlatformLoginErrorMessage = getCustomPlatformLoginErrorMessage(error.reason);
          if (customPlatformLoginErrorMessage) {
            const handled = onCustomPlatformReason?.(error.reason) ?? false;
            if (handled) {
              onSendVerifyCode(false, '');
              return;
            }
            toast({
              type: 'warning',
              message: customPlatformLoginErrorMessage,
            });
            onSendVerifyCode(false, '');
            return;
          }

          switch (error.reason) {
            case 'TooManyRequests':
              errorMsg = t(`${LOGIN_ERROR_INTL_PREFIX}resendExceeded`);
              break;
            case 'FieldInvalid': {
              const localeIdentityType = t(
                `login.${loginSendVerifyCodeInfo.current.type}Placeholder`,
              ).toLocaleLowerCase();
              errorMsg = t(`${LOGIN_ERROR_INTL_PREFIX}identityInvalid`, {
                identityType: localeIdentityType,
              });
              break;
            }
            case 'UserBlocked':
              errorMsg = t(`${LOGIN_ERROR_INTL_PREFIX}banedUser`);
              break;
            case 'EmailDomainDisabled':
              errorMsg = t(`${LOGIN_ERROR_INTL_PREFIX}emailDomainDisabled`);
              break;
            case 'EmailAliasNotSupport':
              errorMsg = t(`${LOGIN_ERROR_INTL_PREFIX}emailAliasNotSupport`);
              break;
            case 'CaptchaNotSupportedOnApp':
              errorMsg = t(`${LOGIN_ERROR_INTL_PREFIX}captchaNotSupportedOnApp`);
              break;
            case 'CaptchaRequired': {
              // The backend carries captcha_id in error metadata (not message),
              // so gateway/SDK/i18n layers that rewrite the human-readable
              // message cannot break the client flow.
              const captchaId = error.metadata?.captcha_id;
              if (!captchaId || !window.showAWSCaptcha || captchaInFlightRef.current) {
                onSendVerifyCode(false, t(`${LOGIN_ERROR_INTL_PREFIX}captchaError`));
                return;
              }
              // Snapshot the request context so a later input change cannot mix
              // an old captcha_id with a new email/channel/customer_code.
              const snapshot = { ...loginSendVerifyCodeInfo.current };
              const channel = snapshot.type === 'email' ? 'email' : 'sms';
              captchaInFlightRef.current = true;
              onSendVerifyCode(true, '');
              window
                .showAWSCaptcha()
                .then(() =>
                  usersVerifyCaptchaGate({
                    body: {
                      captcha_id: captchaId,
                      email: snapshot.value,
                      purpose: 'login',
                      customer_code: snapshot.customerCode,
                      channel,
                    },
                  }),
                )
                .then((gateRes) => {
                  if (gateRes.error) {
                    throw new Error('captcha gate failed');
                  }
                  return usersSendAuthCode({
                    body: {
                      channel,
                      receiver: snapshot.value,
                      purpose: 'login',
                      locale: 'en',
                      customer_code: snapshot.customerCode,
                      captcha_id: captchaId,
                    },
                  });
                })
                .then(({ error: retryError }) => {
                  if (retryError) {
                    onSendVerifyCode(false, t(`${LOGIN_ERROR_INTL_PREFIX}sendVerifyCodeError`));
                  } else {
                    onSendVerifyCode(true, '');
                    onSetCodeError(false);
                    callback?.();
                  }
                })
                .catch(() => {
                  onSendVerifyCode(false, t(`${LOGIN_ERROR_INTL_PREFIX}captchaError`));
                })
                .finally(() => {
                  captchaInFlightRef.current = false;
                });
              return;
            }
            default:
              errorMsg = t(`${LOGIN_ERROR_INTL_PREFIX}sendVerifyCodeError`);
              break;
          }
          onSendVerifyCode(false, errorMsg);
          return;
        }
        onSendVerifyCode(true, '');
        onSetCodeError(false);
        callback?.();
      },
      onError: () => {
        onSendVerifyCode(false, t(`${LOGIN_ERROR_INTL_PREFIX}network`));
      },
    },
  );

  const { run: runSignInWithVerifyCode } = useFetch(
    (params: {
      loginInfo: LoginInfo;
      verifyCode: string;
      inviteCode: string;
      teamInviteCode?: string;
      receive_marketing_msg: boolean | undefined;
      inviter_id?: string;
    }) => {
      return usersLogin({
        body: {
          id_type: params.loginInfo.type,
          identity: params.loginInfo.value,
          auth_type: 'authcode',
          credential: params.verifyCode,
          device_id: getDeviceId(),
          invite_code: params.inviteCode,
          team_invite_code: params.teamInviteCode,
          receive_marketing_msg: params.receive_marketing_msg,
          inviter_id: params.inviter_id,
          im_ref: getImpactImRef(),
          xhs_click_id: getXhsClickId(),
          customer_code: params.loginInfo.customerCode,
        },
      });
    },
    {
      manual: true,
      onSuccess: ({ data, error }, [params]) => {
        if (error) {
          const customPlatformLoginErrorMessage = getCustomPlatformLoginErrorMessage(error.reason);
          if (customPlatformLoginErrorMessage) {
            const handled = onCustomPlatformReason?.(error.reason) ?? false;
            if (handled) {
              onSignInWithVerifyCode(false, '');
              reportEvent('login', {
                login_method: params.loginInfo.type,
                is_success: false,
                failure_reason: error.reason,
              });
              return;
            }
            toast({
              type: 'warning',
              message: customPlatformLoginErrorMessage,
            });
            onSignInWithVerifyCode(false, '');
            reportEvent('login', {
              login_method: params.loginInfo.type,
              is_success: false,
              failure_reason: error.reason,
            });
            return;
          }

          switch (error.reason) {
            case 'AuthCodeInvalid':
              onSetCodeError(true);
              onSignInWithVerifyCode(false, t(`${LOGIN_ERROR_INTL_PREFIX}codeInvalid`));
              break;
            default:
              onSignInWithVerifyCode(false, t(`${LOGIN_ERROR_INTL_PREFIX}unknownError`));
              break;
          }
          reportEvent('login', {
            login_method: params.loginInfo.type,
            is_success: false,
            failure_reason: error.reason,
          });
          return;
        }

        if (data) {
          onSignInWithVerifyCode(true, '');
          if (data.user.status === 'banned') {
            router.replace('/banned');
            return;
          }
          if (skipPostLoginInitialization) {
            updateUserInfo(data.user);
          } else {
            Promise.all([
              updateSubscriptionAsync(),
              updateCreditsAsync(),
              updateTrialsAsync(),
              fetchTrialExpirationAsync(),
              fetchAvailableNexusAsync({
                query: {
                  rule_set: ['sub_one_time_offer', 'mission_watch_ad_credit', 'claw_pass_bundle_first_purchase'],
                },
              }),
              fetchAgentInviteInfoAsync(),
              isEnterpriseDistribution ? undefined : getMyClawsAsync().catch(() => {}),
            ]).then(() => {
              updateUserInfo(data.user);
            });
          }

          sensorsSignUp(data.user.id);
          setRegisterPage({
            uid: data.user.id,
            is_member: data.user.subs_plan !== 'free',
            member_type: data.user.subs_plan,
          });
          if (data.user.is_first_login && SITE === 'global') {
            trackMetaCompleteRegistration();
            const utmParams = processReferrer();
            reportTrack({
              utmSource: utmParams.utm_source || '',
              utmMedium: utmParams.utm_medium || '',
              utmCampaign: utmParams.utm_campaign || '',
              utmTerm: utmParams.utm_term || '',
              utmContent: utmParams.utm_content || '',
            });
          }

          if (data.user.is_first_login) {
            const fromPage = searchParams?.get('from_page');
            if (fromPage && VALID_FROM_PAGES.includes(fromPage as (typeof VALID_FROM_PAGES)[number])) {
              const userEmail = data.user.email || params.loginInfo.value;
              reportEvent('login_page_exposure', {
                from_page: fromPage,
                register: userEmail,
              });
            }
          }
          reportEvent('login', {
            login_method: params.loginInfo.type,
            is_success: true,
            failure_reason: '',
          });
        }
      },
      onError: (error, [params]) => {
        onSignInWithVerifyCode(false, t(`${LOGIN_ERROR_INTL_PREFIX}network`));
        reportEvent('login', {
          login_method: params.loginInfo.type,
          is_success: false,
          failure_reason: error.message || error.name,
        });
      },
      onFinally: () => {
        pendingSignInGateRef.current.release();
      },
    },
  );

  const signInWithVerifyCode = useMemoizedFn((params: Parameters<typeof runSignInWithVerifyCode>[0]) => {
    const pendingKey = `${params.loginInfo.type}:${params.loginInfo.value}:${params.loginInfo.customerCode ?? ''}:${params.verifyCode}`;
    // iOS OTP autofill can fire onComplete twice before React loading state updates.
    if (!pendingSignInGateRef.current.enter(pendingKey)) {
      return;
    }
    runSignInWithVerifyCode(params);
  });

  return {
    sending,
    sendVerifyCode,
    signInWithVerifyCode,
  };
};
